import { useFragment, useReadQuery } from '@apollo/client';
import { EmptyStatePrimaryAction } from '@synoptic/ui-kit/empty-state/empty-state-primary-action.js';
import { EmptyState } from '@synoptic/ui-kit/empty-state/empty-state.js';
import { EmojiSadIcon } from '@synoptic/ui-kit/icons/react/emoji-sad.js';
import { useParams } from 'react-router';
import { LayoutCard } from '@/components/layout/card';
import { Link } from '@/routes-utils/navigation.tsx';
import { paths } from '@/routes-utils/paths';
import { useTournamentLoaderData } from './loader';
import { TournamentBanner } from './tournament-banner/tournament-banner';
import { TournamentBannerFragment } from './tournament-banner/tournament-banner-fragment';
import { TournamentBannerSkeleton } from './tournament-banner/tournament-banner-skeleton';
import { TournamentSubmissionsSubscription } from './tournament-submissions-subscription';
import { notFound } from './page.css';

// We use combination of preloadQuery(, {returnPartialData: true}) with useFragment as a workaround to suspendable fragments
// https://community.apollographql.com/t/usereadquery-but-for-fragment-partial-data/7703

export const TournamentBannerSection = () => {
  const id = useParams().id as string;
  const { queryRef, authenticated } = useTournamentLoaderData();

  const {
    data: { tournament },
  } = useReadQuery(queryRef);

  const { complete, data } = useFragment({
    fragment: TournamentBannerFragment,
    fragmentName: 'TournamentBannerFragment',
    from: {
      __typename: 'Tournament',
      id,
    },
  });

  if (!tournament)
    return (
      <LayoutCard className={notFound}>
        <EmptyState
          illustration={<EmojiSadIcon />}
          heading="Not found"
          description="The tournament you are looking for does not exist."
          action={
            <EmptyStatePrimaryAction asChild>
              <Link to={paths.tournaments} replace>
                Go back
              </Link>
            </EmptyStatePrimaryAction>
          }
        ></EmptyState>
      </LayoutCard>
    );

  if (!complete) return <TournamentBannerSkeleton />;

  return (
    <>
      <TournamentBanner withCta={authenticated} tournament={data} />
      {authenticated && <TournamentSubmissionsSubscription id={data.id} />}
    </>
  );
};
